import { Component, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FilterService, GridComponent, GridModule } from '@syncfusion/ej2-angular-grids';
import { SYNCFUSION_MODULES, ANGULAR_MODULES } from 'src/app/Shared/shared-imports';
import { WordReplacePipe } from 'src/app/Shared/word-replace.pipe';
import { VendorService } from 'src/app/services/Vendor.service';
import { Subject, takeUntil } from 'rxjs';
import Swal from 'sweetalert2';
import printJS from 'print-js';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { GeneralService } from 'src/app/services/general.service';
import { EBaseTemplateID } from 'src/app/Shared/enum';
import { PhoneFormatPipe } from 'src/app/Shared/phone-format.pipe';
import { LoaderComponent } from 'src/app/Shared/loader/loader.component';

@Component({
  selector: 'app-ops-vendor-contracts',
  standalone: true,
  imports: [SYNCFUSION_MODULES, ANGULAR_MODULES, WordReplacePipe, GridModule, PhoneFormatPipe, LoaderComponent],
  templateUrl: './ops-vendor-contracts.html',
  providers: [FilterService],
  styleUrl: './ops-vendor-contracts.scss'
})
export class OpsVendorContracts implements OnInit, OnDestroy {
  @Input() vendorId: number;
  @ViewChild('gridContracts', { static: false }) public gridContracts: GridComponent;

  private destroy$ = new Subject<void>();
  vendorInfo: any;
  public gridData: any[] = [];
  public pdfUrl: SafeResourceUrl | null = null;
  loaderIcon: Promise<boolean>;

  constructor(private vendorService: VendorService, private sanitizer: DomSanitizer, private generalService: GeneralService, private wordReplacePipe: WordReplacePipe) { }

  ngOnInit(): void {
    this.getVendorInfo();
    this.loadVendorContracts();
  }

  loadVendorContracts() {
    this.loaderIcon = Promise.resolve(true);
    this.vendorService.GetVendorContracts(this.vendorId)
      .pipe(takeUntil(this.destroy$))
      .subscribe(res => {
        this.gridData = res.map(el => {
          let RentalType = '';
          switch (el.RentalType) {
            case 1: RentalType = 'Daily'; break;
            case 4: RentalType = 'Monthly'; break;
            case 5: RentalType = 'Weekly'; break;
          }

          this.loaderIcon = Promise.resolve(false);
          return {
            ...el,
            RentalType
          };
        });
        this.loaderIcon = Promise.resolve(false);
      });
  }

  getVendorInfo() {
    this.vendorService.GetVendorById(this.vendorId)
      .pipe(takeUntil(this.destroy$))
      .subscribe(res => {
        if (res) this.vendorInfo = res;
      });
  }

  onPrint(row: any) {
    const accountReceiptId = row.AccountReceiptId;

    this.loaderIcon = Promise.resolve(true);
    this.vendorService.GenerateContractForAccount(accountReceiptId).subscribe({
      next: (response: any) => {
        const pdfDataUrl = response?.Message;
        if (!pdfDataUrl || !pdfDataUrl.startsWith('data:application/pdf;base64,')) {
          Swal.fire('Error', 'Invalid PDF data in response', 'error');
          this.loaderIcon = Promise.resolve(false);
          return;
        }

        const pdfBase64 = pdfDataUrl.split(',')[1];
        printJS({ printable: pdfBase64, type: 'pdf', base64: true });
        this.loaderIcon = Promise.resolve(false);
      },
      error: () => {
        Swal.fire('Error', 'Failed to generate contract for print', 'error')
        this.loaderIcon = Promise.resolve(false);
      }
    });
  }

  onView(row: any) {
    const accountReceiptId = row.AccountReceiptId;

    this.loaderIcon = Promise.resolve(true);
    this.pdfUrl = null;

    this.vendorService.GenerateContractForAccount(accountReceiptId).subscribe({
      next: (response: any) => {
        const pdfDataUrl = response?.Message;
        if (!pdfDataUrl || !pdfDataUrl.startsWith('data:application/pdf;base64,')) {
          Swal.fire('Error', 'Invalid PDF data in response', 'error');
          this.loaderIcon = Promise.resolve(false);
          return;
        }

        const pdfBase64 = pdfDataUrl.split(',')[1];
        const blob = this.base64ToBlob(pdfBase64, 'application/pdf');
        this.pdfUrl = this.sanitizer.bypassSecurityTrustResourceUrl(URL.createObjectURL(blob));

        this.loaderIcon = Promise.resolve(false);

        const modalEl = document.getElementById('viewContractModal');
        if (modalEl) {
          const modalInstance = (window as any)['bootstrap'].Modal.getOrCreateInstance(modalEl);
          modalInstance.show();
        }
      },
      error: () => {
        Swal.fire('Error', 'Failed to load contract for view', 'error');
        this.loaderIcon = Promise.resolve(false);
      }
    });
  }

  onEmail(row: any) {
    const accountReceiptId = row.AccountReceiptId;
    const baseTemplateId = EBaseTemplateID.RentalContract;
    const fieldName = this.wordReplacePipe.transform('Vendor');
    const subject = `Contract for ${fieldName} ${this.vendorInfo?.VendorName || ''}`;

    Swal.fire({
      title: 'Send Email',
      text: `Are you sure you want to email this contract to the ${fieldName}?`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Yes, send it',
    }).then((result) => {
      if (!result.isConfirmed) return;

      this.loaderIcon = Promise.resolve(true);
      this.vendorService.GenerateContractForAccount(accountReceiptId).subscribe({
        next: (response: any) => {
          const pdfDataUrl = response?.Message;

          if (!pdfDataUrl || !pdfDataUrl.startsWith('data:application/pdf;base64,')) {
            Swal.fire('Error', 'Invalid PDF data in response', 'error');
            this.loaderIcon = Promise.resolve(false);
            return;
          }

          const fileName = `RentalContract.pdf`;

          const emailData = [{
            AccountReceiptID: accountReceiptId,
            VendorID: parseInt(this.vendorId.toString(), 10),
            BaseTemplateID: baseTemplateId,
            AttachmentFile: pdfDataUrl,
            AttachmentFileName: fileName,
            FileType: 'application/pdf',
            Subject: subject
          }];

          this.generalService.composeEmails(emailData).subscribe({
            next: (res: any) => {
              if (res?.Success) {
                Swal.fire('Success', 'Contract emailed successfully!', 'success');
                this.loaderIcon = Promise.resolve(false);
              } else {
                Swal.fire('Error', res?.Message || 'Email sending failed', 'error');
                this.loaderIcon = Promise.resolve(false);
              }
            },
            error: (err) => {
              console.error('Email API error:', err);
              Swal.fire('Error', 'Failed to send email due to server issue', 'error');
              this.loaderIcon = Promise.resolve(false);
            }
          });
        },
        error: () => {
          Swal.fire('Error', 'Failed to generate contract for email', 'error');
          this.loaderIcon = Promise.resolve(false);
        }
      });
    });
  }

  private base64ToBlob(base64: string, type: string): Blob {
    const byteCharacters = atob(base64);
    const byteArray = new Uint8Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteArray[i] = byteCharacters.charCodeAt(i);
    }
    return new Blob([byteArray], { type });
  }

  public created(args1) {
    this.gridContracts.element.querySelectorAll('.e-filterdiv')[this.gridContracts.getColumnIndexByField('print')].classList.add('e-hide');
    this.gridContracts.element.querySelectorAll('.e-filterdiv')[this.gridContracts.getColumnIndexByField('email')].classList.add('e-hide');
    this.gridContracts.element.querySelectorAll('.e-filterdiv')[this.gridContracts.getColumnIndexByField('view')].classList.add('e-hide');
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
